import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ClockIcon } from "../../../../assets/Oclock";
import {
  formatPromotionLeft,
  getPromotionMsLeft,
} from "../../../../utils/promotionCountdown";

type IProps = {
  serverTime?: string | null;
  endDate?: string | null;
  /** Только красная плашка, без подписи «До конца акции» слева. */
  badgeOnly?: boolean;
};

// Отсчёт идёт в минутах, поэтому чаще раза в полминуты пересчитывать незачем.
const TICK_MS = 30 * 1000;

export const PromotionCountdown = ({
  serverTime,
  endDate,
  badgeOnly = false,
}: IProps) => {
  const { t } = useTranslation();

  // Остаток на момент ответа бэка — дальше только вычитаем прошедшее время.
  const initialMsLeft = useMemo(
    () => getPromotionMsLeft(serverTime, endDate),
    [serverTime, endDate],
  );
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    // Нечего отсчитывать: дат нет или акция уже закончилась.
    if (!initialMsLeft) return;

    // Момент старта фиксируем здесь: разницу двух Date.now() перевод часов
    // и смена таймзоны не ломают.
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const passed = Date.now() - startedAt;
      setElapsed(passed);

      // Досчитали до нуля — плашка скрывается, тикать дальше незачем.
      if (passed >= initialMsLeft) clearInterval(timer);
    }, TICK_MS);

    return () => clearInterval(timer);
  }, [initialMsLeft]);

  if (initialMsLeft === null) return null;

  const msLeft = Math.max(0, initialMsLeft - elapsed);

  // Акция кончилась — плашку убираем, а не показываем "0м".
  if (msLeft === 0) return null;

  const badge = (
    <div className="flex w-fit flex-row items-center gap-x-1 h-6 px-2 rounded-full bg-fill-danger text-text-white">
      <ClockIcon />
      <span className="text-caption-sm-medium">
        {formatPromotionLeft(msLeft, {
          day: t("unitDayShort"),
          hour: t("unitHourShort"),
          minute: t("unitMinuteShort"),
        })}
      </span>
    </div>
  );

  if (badgeOnly) return badge;

  return (
    <div className="flex flex-row items-center justify-between">
      <p className="text-body-sm-regular text-text-secondary">
        {t("promotionEndsIn")}
      </p>
      {badge}
    </div>
  );
};
